import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';

// Sends the visitor to an external URL on mount. Used for /onboard which redirects
// to the new-client application form on app.kgob.com.
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      color: '#0B2240',
    }}>
      Taking you to the KGOB onboarding form&hellip;
    </div>
  );
};
import Home from '@/pages/Home';
import Contact from '@/pages/Contact';
import Services from '@/pages/Services';
import SmartServiceSelector from '@/pages/SmartServiceSelector';
import AccountingPayroll from '@/pages/AccountingPayroll';
import TaxServices from '@/pages/TaxServices';
import IRSRelief from '@/pages/IRSRelief';
import EntityServices from '@/pages/EntityServices';
import BusinessValuation from '@/pages/BusinessValuation';
import StrategicConsulting from '@/pages/StrategicConsulting';
import FinancialTools from '@/pages/FinancialTools';
import Community from '@/pages/Community';
import PanthersPartnership from '@/pages/PanthersPartnership';
import Campaign52Landing from '@/pages/campaign52/Landing';
import Campaign52Results from '@/pages/campaign52/Results';
import HowWeWork from '@/pages/HowWeWork';
import OurStory from '@/pages/OurStory';
// import LetsTalkTaxes from '@/pages/LetsTalkTaxes'; // campaign paused Aug 2026
import LttBookingDetail from '@/pages/LttBookingDetail';
import ReferUs from '@/pages/ReferUs';
import ReferralDetail from '@/pages/ReferralDetail';
import PreRegister from '@/pages/PreRegister';
import Coffee from '@/pages/Coffee';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import LeadBoard from '@/pages/LeadBoard';
import LeadDetail from '@/pages/LeadDetail';
import ConsultationDetail from '@/pages/ConsultationDetail';
import BookConsultationRedirect from '@/pages/BookConsultationRedirect';
import SignIn from '@/pages/SignIn';
import ManageBooking from '@/pages/ManageBooking';
import TeamRegister from '@/pages/TeamRegister';
import Holiday from '@/pages/Holiday';
import HolidayThankYou from '@/pages/HolidayThankYou';
// import MeetKGOBLetsTalkTaxes from '@/pages/MeetKGOBLetsTalkTaxes'; // campaign paused Aug 2026
import MeetKGOBThankYou from '@/pages/MeetKGOBThankYou';
import ReferralLanding from '@/pages/ReferralLanding';
import ICApplication from '@/pages/ICApplication';
import IRSTranscriptReport from '@/pages/IRSTranscriptReport';
import Welcome from '@/pages/Welcome';
import FAQ from '@/pages/FAQ';
import EmailPreferences from '@/pages/EmailPreferences';
import GetYourAnswer from '@/pages/GetYourAnswer';
import GetYourAnswerLibrary from '@/pages/GetYourAnswerLibrary';
import GetYourAnswerQuestion from '@/pages/GetYourAnswerQuestion';
import GetYourAnswerAsk from '@/pages/GetYourAnswerAsk';
import Locations from '@/pages/Locations';
import AreaPage from '@/pages/locations/AreaPage';
import JournalIndex from '@/pages/journal/JournalIndex';
import JournalArchive from '@/pages/journal/JournalArchive';
import JournalArticle from '@/pages/journal/JournalArticle';
import LandingPage from '@/pages/lp/LandingPage';
import LandingPageThankYou from '@/pages/lp/LandingPageThankYou';
import BusinessHealthCheckLanding from '@/pages/health-check/Landing';
import PenaltyRecoveryLanding from '@/pages/penalty-recovery/PenaltyRecoveryLanding';
import PenaltyRecoveryTerms from '@/pages/penalty-recovery/PenaltyRecoveryTerms';
import PenaltyRecoveryRefund from '@/pages/penalty-recovery/PenaltyRecoveryRefund';
import PenaltyRecoveryThankYou from '@/pages/penalty-recovery/PenaltyRecoveryThankYou';
import NotFound from '@/pages/NotFound';
import { EventsRouter } from '@/events';
import TrustEstateHealthCheck from '@/pages/health-check/TrustEstate';
import HealthCheckQualifier from '@/pages/health-check/Qualifier';
import HealthCheckCustomQuote from '@/pages/health-check/CustomQuote';
import HealthCheckTerms from '@/pages/health-check/Terms';
import HealthCheckCheckout from '@/pages/health-check/Checkout';
import HealthCheckThankYou from '@/pages/health-check/ThankYou';
import HealthCheckSchedule from '@/pages/health-check/Schedule';
import HealthCheckPortal from '@/pages/health-check/Portal';
import { ScrollToTop } from '@/components/ScrollToTop';
import { RouteChangeTracker } from '@/components/analytics/RouteChangeTracker';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

function App() {
  // Prerendered static HTML bakes a canonical (no data-rh) into <head>. When the
  // static host serves a parent route's file for a non-prerendered deep URL
  // (e.g. Q&A variants), that baked canonical is WRONG for the actual page.
  // Helmet adds the correct one on hydration; remove the baked tag so Google
  // never sees two conflicting canonicals.
  useEffect(() => {
    document.querySelectorAll('link[rel="canonical"]:not([data-rh])').forEach((el) => el.remove());
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <RouteChangeTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/selector" element={<SmartServiceSelector />} />
            <Route path="/services/accounting-payroll" element={<AccountingPayroll />} />
            <Route path="/services/tax" element={<TaxServices />} />
            <Route path="/services/irs-relief" element={<IRSRelief />} />
            <Route path="/services/entity" element={<EntityServices />} />
            <Route path="/services/valuation" element={<BusinessValuation />} />
            <Route path="/services/consulting" element={<StrategicConsulting />} />
            <Route path="/services/tools" element={<FinancialTools />} />
            <Route path="/community" element={<Community />} />
            <Route path="/panthers-partnership" element={<PanthersPartnership />} />
            <Route path="/52" element={<Campaign52Landing />} />
            <Route path="/52/results" element={<Campaign52Results />} />
            <Route path="/52/*" element={<Navigate to="/52" replace />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/our-story" element={<OurStory />} />
            {/* LetsTalkTaxes campaign PAUSED (Aug 2026, user request) — restore by
                swapping Navigate back to <LetsTalkTaxes /> / <MeetKGOBLetsTalkTaxes /> */}
            <Route path="/letstalktaxes" element={<Navigate to="/contact" replace />} />
            <Route path="/refer-us" element={<ReferUs />} />
            <Route path="/pre-register" element={<PreRegister />} />
            <Route path="/coffee" element={<Coffee />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/email-preferences" element={<EmailPreferences />} />
            <Route path="/get-your-answer" element={<GetYourAnswer />} />
            <Route path="/get-your-answer/ask" element={<GetYourAnswerAsk />} />
            <Route path="/get-your-answer/library" element={<GetYourAnswerLibrary />} />
            <Route path="/get-your-answer/:topicId/:slug" element={<GetYourAnswerQuestion />} />
            <Route path="/book-consultation" element={<BookConsultationRedirect />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/book-consultation/manage/:token" element={<ManageBooking />} />
            <Route path="/holiday" element={<Holiday />} />
            <Route path="/holiday/thank-you" element={<HolidayThankYou />} />
            <Route path="/meetKGOB-LetsTalktaxes" element={<Navigate to="/contact" replace />} />
            <Route path="/meetKGOB-LetsTalktaxes/thank-you" element={<MeetKGOBThankYou />} />
            <Route path="/leadboard/leads/:leadId" element={<LeadDetail />} />
            <Route path="/leadboard/consultations/:consultationId" element={<ConsultationDetail />} />
            <Route path="/leadboard/letstalktaxes/:bookingId" element={<LttBookingDetail />} />
            <Route path="/leadboard/referrals/:referralId" element={<ReferralDetail />} />
            <Route path="/leadboard" element={<LeadBoard />} />
            <Route path="/team/register" element={<TeamRegister />} />
            <Route path="/refer/:code" element={<ReferralLanding />} />
            <Route path="/apply-for-ICP" element={<Navigate to="/cash" replace />} />
            {/* Legacy URLs still crawled by Google (GSC noindex report) — redirect to real pages */}
            <Route path="/about" element={<Navigate to="/our-story" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/careers" element={<Navigate to="/contact" replace />} />
            <Route path="/onboard" element={<ExternalRedirect to="https://app.kgob.com/onboard" />} />
            <Route path="/cash" element={<ICApplication />} />
            <Route path="/irs-account-review" element={<IRSTranscriptReport />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:slug" element={<AreaPage />} />
            <Route path="/north-carolina-cpa" element={<AreaPage slug="north-carolina-cpa" />} />
            <Route path="/south-carolina-cpa" element={<AreaPage slug="south-carolina-cpa" />} />
            <Route path="/journal" element={<JournalIndex />} />
            <Route path="/journal/archive" element={<JournalArchive />} />
            <Route path="/journal/:id" element={<JournalArticle />} />
            {/* PPC Ads Landing Pages — isolated source #9 (ppc_ads). See /app/docs/PPC_ADS_LP_INTERNAL_SPEC.md */}
            <Route path="/lp/:slug/thank-you" element={<LandingPageThankYou />} />
            <Route path="/lp/:slug" element={<LandingPage />} />
            <Route path="/business-health-check" element={<BusinessHealthCheckLanding />} />
            <Route path="/business-health-check/trust-estate" element={<TrustEstateHealthCheck />} />
            <Route path="/business-health-check/qualifier" element={<HealthCheckQualifier />} />
            <Route path="/business-health-check/custom-quote" element={<HealthCheckCustomQuote />} />
            <Route path="/business-health-check/terms" element={<HealthCheckTerms />} />
            <Route path="/business-health-check/checkout" element={<HealthCheckCheckout />} />
            <Route path="/business-health-check/thank-you" element={<HealthCheckThankYou />} />
            <Route path="/business-health-check/schedule" element={<HealthCheckSchedule />} />
            <Route path="/business-health-check/manage" element={<HealthCheckPortal />} />
            <Route path="/penalty-recovery" element={<PenaltyRecoveryLanding />} />
            <Route path="/penalty-recovery/termsofservice" element={<PenaltyRecoveryTerms />} />
            <Route path="/penalty-recovery/refundanddisputepolicy" element={<PenaltyRecoveryRefund />} />
            <Route path="/penalty-recovery/thank-you" element={<PenaltyRecoveryThankYou />} />
            {/* One splat route: the events module has its own descendant <Routes>, and a bare "/events"
                parent makes React Router warn and never match the deeper paths. */}
            <Route path="/events/*" element={<EventsRouter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </div>
    </HelmetProvider>
  );
}

export default App;